/**
 * Usuários da loja (role `Validator`) — paridade com a tela `/validators` do dash legado.
 *
 * Usa os MESMOS endpoints da legacy-api que o legado usava, sem alteração de API:
 *   listar   → POST   /users            (User.list_by_type, filtra por id_passbook + role)
 *   salvar   → POST   /auth/register    (mesmo controller de insert/update, via `action`)
 *   vincular → POST   /client_user      (Client.save_by_passbook — só na criação)
 *   remover  → DELETE /user/:id_passbook/:id_user/:removeData
 *
 * Referência: legacy/dash/routes/validators.js e legacy/dash/routes/validator/*.js
 */
import { legacy } from '@/shared/api/client';

/** Documento de usuário como a legacy-api devolve em `POST /users`. */
export interface StoreUser {
  _id: string;
  email: string;
  /** Hash bcrypt. Ver a nota sobre senha em `saveStoreUser`. */
  password?: string;
  profile?: {
    name?: string;
    position?: string;
  };
}

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

/**
 * O legado chama isto de dentro do DataTables (`POST /validators/data/:id`).
 * O filtro server-side já exclui `deletedMember` e quem não tem `profile.name`.
 */
export async function fetchStoreUsers(idPassbook: string): Promise<StoreUser[]> {
  const data = await legacy.post<StoreUser[]>(
    '/users',
    { id_passbook: idPassbook, role: ['Validator'] },
    passbookHeaders(idPassbook),
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Espelha `routes/validator/edit.js`: o formulário de edição precisa do documento
 * completo, incluindo o hash da senha (ver a nota em `saveStoreUser`).
 */
export async function fetchStoreUser(idPassbook: string, idUser: string): Promise<StoreUser> {
  return legacy.get<StoreUser>(`/user/${idUser}`, passbookHeaders(idPassbook));
}

export interface StoreUserPayload {
  idPassbook: string;
  /** Vazio/ausente = criação. Preenchido = edição do usuário com esse `_id`. */
  id?: string;
  name: string;
  email: string;
  /**
   * Na criação, a senha em texto puro.
   * Na edição, ou a nova senha em texto puro, ou o hash atual (mantém inalterada).
   */
  password: string;
  position: string;
}

interface RegisterResponse {
  user?: { _id?: string };
}

/**
 * Espelha `routes/validator/save.js`: registra e, quando é criação, faz a segunda
 * chamada que vincula o usuário ao cliente da loja. Sem esse vínculo o usuário é
 * criado mas não passa a pertencer à loja.
 *
 * Sobre a senha na edição — o update em `AuthenticationController.register` faz
 * `user.password = password` sempre, e o model tem `pre('save')` que só re-hasheia
 * quando `isModified('password')`. Logo:
 *   - reenviar o hash atual → Mongoose não vê mudança → senha preservada;
 *   - enviar texto puro novo → re-hasheia → senha trocada;
 *   - omitir → `user.password = undefined` → apagaria a senha. Nunca fazer.
 * É por isso que a UI reenvia o hash quando o campo é deixado em branco.
 */
export async function saveStoreUser(payload: StoreUserPayload): Promise<void> {
  const creating = !payload.id;

  const registered = await legacy.post<RegisterResponse>(
    '/auth/register',
    {
      action: creating ? 'insert' : 'update',
      id_passbook: payload.idPassbook,
      id: payload.id ?? '0',
      name: payload.name,
      email: payload.email,
      password: payload.password,
      position: payload.position,
      role: 'Validator',
    },
    passbookHeaders(payload.idPassbook),
  );

  if (!creating) return;

  const createdId = registered?.user?._id;
  if (!createdId) throw new Error('A API não devolveu o usuário criado.');

  await legacy.post(
    '/client_user',
    { id_passbook: payload.idPassbook, id_user: createdId },
    passbookHeaders(payload.idPassbook),
  );
}

/**
 * ATENÇÃO — remoção definitiva.
 *
 * O `true` final é o parâmetro `removeData` de `DELETE /user/:id_passbook/:id_user/:removeData`.
 * Com `true`, a legacy-api roda `User.remove()` e apaga o documento do banco; com `false` ela
 * apenas marca `deletedMember: true`. O botão "Remover" do dash legado sempre mandou `true`,
 * e mantivemos a paridade — a confirmação da UI deixa isso explícito para o usuário.
 */
export async function deleteStoreUser(idPassbook: string, idUser: string): Promise<void> {
  await legacy.delete(`/user/${idPassbook}/${idUser}/true`, passbookHeaders(idPassbook));
}
